import { useCallback, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { ENTITY_TYPES } from '@sales-center/common'
import { useGetClosedReasons } from './useGetClosedReasons'
import { FormFieldsProps } from '../FormFields'
import { BulkUpdateStatusFormModel } from '../typings'

export const useFormFieldActions = (props: FormFieldsProps) => {
  const { salesFunnelStages, entityType } = props

  const {
    values: { salesFunnelId, salesFunnelStageId },
    setValues,
    isValidating,
  } = useFormikContext<BulkUpdateStatusFormModel>()

  const onChangeSalesFunnelId = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues({
        salesFunnelId: event.target.value,
        salesFunnelStageId: '',
        closedReason: '',
        customReason: '',
      })
    },
    [setValues],
  )

  const onChangeSalesFunnelStageId = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((current) => ({
        ...current,
        salesFunnelStageId: event.target.value,
        closedReason: '',
        customReason: '',
      }))
    },
    [setValues],
  )

  const onCustomReasonFocus = useCallback(() => {
    setValues((current) => ({
      ...current,
      closedReason: '0',
    }))
  }, [setValues])

  const activeStages = useMemo(() => {
    const sectionType =
      entityType === ENTITY_TYPES.Lead ? 'Lead' : 'Opportunity'

    return salesFunnelStages.filter(
      (s) =>
        s.sectionType === sectionType &&
        s.salesFunnelId === Number(salesFunnelId),
    )
  }, [entityType, salesFunnelId, salesFunnelStages])

  const { data: closedReasons, isFetching: closedReasonsLoading } =
    useGetClosedReasons(salesFunnelId, entityType)

  const displayClosedReasons = useMemo(() => {
    const stage = activeStages.find((x) => x.id === Number(salesFunnelStageId))

    return !!stage ? stage.closed && stage.name !== 'Closed/Won' : false
  }, [salesFunnelStageId, activeStages])

  const disableInteraction = useMemo(
    () => closedReasonsLoading || isValidating,
    [closedReasonsLoading, isValidating],
  )

  return {
    activeStages,
    closedReasons,
    closedReasonsLoading,
    displayClosedReasons,
    onChangeSalesFunnelId,
    onChangeSalesFunnelStageId,
    onCustomReasonFocus,
    disableInteraction,
    isValidating,
  }
}
